/**
 * Icon — the קוֹל monochrome line-icon set (retires emoji for a consistent,
 * premium feel; research: 2026 premium UI uses one coherent line-icon family).
 * Stroke uses currentColor so callers set color via Tailwind text-* classes.
 */
import type { SVGProps } from 'react';

export type IconName =
  | 'fuel'
  | 'battery'
  | 'engine'
  | 'compass'
  | 'folder'
  | 'activity'
  | 'bluetooth'
  | 'warning'
  | 'shield'
  | 'calendar'
  | 'mic'
  | 'chevron'
  | 'arrow'
  | 'close'
  | 'lock'
  | 'check'
  | 'wrench'
  | 'plus'
  | 'sliders'
  | 'users'
  | 'star'
  | 'camera'
  | 'file'
  | 'trash'
  | 'edit'
  | 'download';

const PATHS: Record<IconName, JSX.Element> = {
  fuel: (
    <>
      <rect x="4" y="4" width="9" height="16" rx="1.5" />
      <path d="M4 11h9M16 8l2 2v7a1.5 1.5 0 0 0 3 0V9l-3-3" />
    </>
  ),
  battery: (
    <>
      <rect x="3" y="8" width="15" height="9" rx="2" />
      <path d="M21 11v3M8 10.5v4M6 12.5h4" />
    </>
  ),
  engine: (
    <>
      <path d="M5 17a8 8 0 1 1 14 0" />
      <path d="M12 13l4-3" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M15 9l-2.5 5L9 15l2.5-5z" />
    </>
  ),
  folder: <path d="M4 7a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />,
  activity: <path d="M3 12h4l2-6 4 12 2-6h6" />,
  bluetooth: <path d="M8 7l8 10-4 3V4l4 3-8 10" />,
  warning: (
    <>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  shield: <path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" />,
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2.5" />
      <path d="M4 9h16M8 3v4M16 3v4" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </>
  ),
  chevron: <path d="M15 6l-6 6 6 6" />,
  arrow: <path d="M9 6l6 6-6 6" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  check: <path d="M5 12l4 4 10-10" />,
  wrench: <path d="M14 4l4 4M3 21l1-4L15 6l3 3L7 20z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  sliders: (
    <>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="15" cy="7" r="2.2" />
      <circle cx="8" cy="17" r="2.2" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5" />
      <path d="M16 5a3.2 3.2 0 0 1 0 6.4M21 20c0-2.6-1.5-4.2-3.8-4.8" />
    </>
  ),
  star: <path d="M12 3.5l2.6 5.6 6 .7-4.5 4.1 1.2 6L12 17.2 6.7 19.9l1.2-6L3.4 9.8l6-.7z" />,
  camera: (
    <>
      <path d="M4 8a2 2 0 0 1 2-2h2l1.5-2h5L18 6h0a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
      <circle cx="12" cy="13" r="3.4" />
    </>
  ),
  file: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M8.5 13h7M8.5 16.5h7" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4L19 9l-4-4L4 16z" />
      <path d="M14 6l4 4" />
    </>
  ),
  download: <path d="M12 4v11M8 11l4 4 4-4M5 19h14" />,
};

export function Icon({
  name,
  size = 22,
  className,
  ...rest
}: { name: IconName; size?: number; className?: string } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
